import { useReducer, useEffect, useContext } from 'react'
import {
    UniversalModal,
    UniversalDataTable,
    UniversalLoadingCircle,
    Grid,
} from '../../../UniversalComponents'

import {
    columnMapper,
    permissionsCheck,
} from '../../../../utilities/functionTools'

import { getProvisionalBoats } from '../../../../apiRoutes'
import {
    useAuth,
    usePermissions,
} from '../../../../context/AuthenticationContext'
import { ToastMessage } from '../../../../context/ToastPopUpContext'

import BoatConfigurationForm from '../BoatConfiguration/BoatConfigForms/BoatConfigurationForm'

import { Alert, Box, Typography } from '@mui/material'
import { useGlobalState } from '../../../../context/GlobalStateContext'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const today = new Date().toISOString().substring(0, 10)

const BoatProvisioning = () => {
    const setToastMessage = useContext(ToastMessage)
    const authentication = useAuth()
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer } = oupUser

    const { setUser } = useAuth()

    const { auth } = authentication
    const currentUser = useAuth()
    const { userProfile } = currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile
    const { username, userPreferences, _id } = auth

    const defaultState = {
        open: false,
        row: {},
        data: [],
        boats: {
            boatProvisioning: {
                columns: [
                    {
                        field: 'fullHin',
                        headerName: ' HIN',
                        width: 300,
                        type: 'string',
                    },
                    {
                        field: 'shortHin',
                        headerName: ' Short HIN',
                        width: 300,
                        type: 'string',
                    },

                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 300,
                        type: 'string',
                    },

                    {
                        field: 'deviceSerial',
                        headerName: 'DEVICE SERIAL',
                        width: 1200,
                        type: 'string',
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.boats?.boatProvisioning
                ?.density || 'standard',
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const userPermissions = usePermissions()

    const { open, row, columnVisibility, data, boats, density } = state

    const { boatProvisioning } = boats

    const getData = async (active) => {
        try {
            const data = await getProvisionalBoats(active)

            if (data && data?.length !== 0) {
                const boats = columnMapper(defaultColumns, data)

                setState({ data: boats })
            } else {
                setState({ data: [] })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const boats =
            userPreferences?.dataTableColumns?.boats?.boatProvisioning?.columns

        if (Array.isArray(boats)) {
            const mapColumns = boats.map((boat) => ({
                field: boat?.field,
                headerName: boat?.headerName,
                width: boat?.width,
                isVisible: boat.isVisible,
                type: boat?.type,
            }))

            const format = {
                boatProvisioning: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, boats: format })
        }
    }
    const submitColumnData = async () => {
        try {
            setUser({
                key: 'boats',
                value: boats,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    useEffect(() => {
        collectColumnData()
        getData(true)
    }, [])

    const defaultColumns = [
        'manufacturer.manufacturerName',
        'device.deviceSerial',
        'device.iccid',
        'device._id',
        'createdAt',
        'shortHin',
        'fullHin',
        'updatedBy',
        'updatedAt',
        'tags',
        'provisionalBoat',
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
            type: col.type,
        }))

        const setDensity = state.density

        const setData = {
            boatProvisioning: { columns: format, density: setDensity },
        }

        setState({
            boats: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            boats: {
                boatProvisioning: {
                    ...boatProvisioning,
                    density: newDensity,
                },
            },
        })
    }

    const manufacturerCheck = activeManufacturer?._id === row?.manufacturer?._id

    return (
        <Box sx={{ width: '100%' }}>
            <UniversalLoadingCircle
                data={data}
                reloadFunction={() => getData(true)}
                customTimeoutMessage={'No Provisioned Boats Available'}
            >
                <UniversalDataTable
                    data={data}
                    apiDensity={density}
                    apiColumns={boats?.boatProvisioning?.columns}
                    checkboxSelection={false}
                    savePreferences={() => submitColumnData()}
                    visibleColumns={[]}
                    getRowData={(tableRow) =>
                        setState({
                            open: true,
                            row: tableRow,
                        })
                    }
                    columnsAvailable={columnVisibility}
                    onUpdatedColumnsChange={handleUpdatedColumnsChange}
                    onDensityChange={handleDensityChange}
                    sx={{ width: 10 }}
                />
            </UniversalLoadingCircle>

            <UniversalModal
                title={'Configure a Boat'}
                open={open}
                onClose={() => setState({ open: false })}
                timeStamp={`${
                    row.fullHin
                        ? ` ${
                              row.updatedBy
                                  ? ` Modified By ${row.updatedBy}`
                                  : ''
                          } ${
                              row.updatedAt
                                  ? `| Modified Date ${row.updatedAt.substring(
                                        0,
                                        10
                                    )}`
                                  : ''
                          }  `
                        : ` Modified By: ${username} | Modified Date: ${today} `
                }`}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 10,
                    marginBottom: 5,
                }}
                draggable={true}
                minimizable={true}
            >
                {!manufacturerCheck ? (
                    <Grid item xs={12} md={12} lg={12}>
                        <Box
                            sx={{
                                maxWidth: 720,
                                mx: 'auto',
                                mt: 8,
                                mb: 10,
                            }}
                        >
                            <Alert
                                severity="warning"
                                icon={<WarningAmberIcon fontSize="inherit" />}
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    py: 2,
                                    '& .MuiAlert-message': {
                                        width: '100%',
                                        textAlign: 'center',
                                    },
                                    '& .MuiAlert-icon': {
                                        fontSize: 32,
                                    },
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 600,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    Please verify your{' '}
                                    <strong>Active Manufacturer</strong> matches
                                    the <strong>Boat’s Manufacturer</strong>.
                                </Typography>
                            </Alert>
                        </Box>
                    </Grid>
                ) : (
                    <BoatConfigurationForm
                        row={row}
                        superAdmin={superAdmin}
                        formDescription="Enter Boat Details To Add A New Boat."
                        onClose={() => {
                            getData(true)
                            setState({ open: false })
                        }}
                        saveCache={(data: any) => setState({ formCache: data })}
                        permissionCheck={permissionsCheck(
                            userProfilePermissions,
                            'Boats'
                        )}
                    />
                )}
            </UniversalModal>
        </Box>
    )
}
export default BoatProvisioning
